
import { useState, useEffect } from 'react';
import { ViewMode } from "@/components/common/ViewToggle";

/**
 * Retrieves the saved view mode for a specific entity type
 * @param entityType The entity type to get the view mode for (e.g., 'candidates', 'companies')
 * @param defaultMode The default view mode to return if none is saved
 * @returns The saved view mode or the default
 */
export const getSavedViewMode = (entityType: string, defaultMode: ViewMode = "card"): ViewMode => {
  const savedViewMode = localStorage.getItem(`${entityType}-view-mode`);
  return (savedViewMode as ViewMode) || defaultMode;
};

/**
 * Saves the view mode for a specific entity type
 * @param entityType The entity type to save the view mode for
 * @param viewMode The view mode to save
 */
export const saveViewMode = (entityType: string, viewMode: ViewMode): void => {
  localStorage.setItem(`${entityType}-view-mode`, viewMode);
};

/**
 * Custom hook to manage view mode persistence
 * @param entityType The entity type to manage view mode for
 * @param defaultMode The default view mode
 * @returns The current view mode and a setter function
 */
export const usePersistedViewMode = (entityType: string, defaultMode: ViewMode = "card"): [ViewMode, (mode: ViewMode) => void] => {
  const [viewMode, setViewMode] = useState<ViewMode>(() => getSavedViewMode(entityType, defaultMode));
  
  useEffect(() => {
    saveViewMode(entityType, viewMode);
  }, [entityType, viewMode]);
  
  return [viewMode, setViewMode];
};
